import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input, Label } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ArrowLeftRight, BookOpen, ChevronDown, ChevronRight, FlaskConical, Save, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

const sgd = (n) => `S$${Math.round(n).toLocaleString()}`;

const LEVER_ROWS = [
  { key: "carbon_price_sgd", label: "Carbon price", fmt: (v) => `S$${v}/t` },
  { key: "hazard_uplift", label: "Hazard uplift", fmt: (v) => `+${v}` },
  { key: "share_affected_pct", label: "Operations affected", fmt: (v) => `${v}%` },
  { key: "mitigation_in_place", label: "Mitigation in place", fmt: (v) => (v ? "Yes" : "No") },
];

function CompareRuns({ a, b }) {
  const fig = (r, key) => r.figures.find((f) => f.key === key)?.value_sgd ?? 0;
  const delta = (va, vb) => {
    const df = vb - va;
    if (Math.round(df) === 0) return <span className="text-muted-foreground">—</span>;
    const pct = va ? ` (${df > 0 ? "+" : ""}${((df / va) * 100).toFixed(0)}%)` : "";
    return <span className={df > 0 ? "text-red-400" : "text-[#a3e635]"}>{df > 0 ? "+" : "−"}{sgd(Math.abs(df))}{pct}</span>;
  };
  const cell = "px-2 py-1 text-right font-mono";
  return (
    <div className="mt-3 border border-accent/40 rounded-sm p-2.5 overflow-x-auto" data-testid="sd-compare-panel">
      <div className="label-eyebrow text-[9px] mb-2 flex items-center gap-1"><ArrowLeftRight className="h-3 w-3 text-accent" /> Comparison — Run A vs Run B</div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-left px-2 py-1 font-normal"></th>
            <th className={`${cell} font-normal`}>A · {new Date(a.created_at).toLocaleDateString()}</th>
            <th className={`${cell} font-normal`}>B · {new Date(b.created_at).toLocaleDateString()}</th>
            <th className={`${cell} font-normal`}>Δ (B − A)</th>
          </tr>
        </thead>
        <tbody>
          {LEVER_ROWS.map((l) => {
            const va = a.levers[l.key], vb = b.levers[l.key];
            const differs = String(va) !== String(vb);
            return (
              <tr key={l.key} className={`border-b border-border/50 ${differs ? "bg-accent/5" : ""}`} data-testid={`sd-cmp-lever-${l.key}`}>
                <td className="px-2 py-1 text-muted-foreground">{l.label} {differs && <Badge variant="lime" className="text-[8px] ml-1">differs</Badge>}</td>
                <td className={cell}>{l.fmt(va)}</td>
                <td className={cell}>{l.fmt(vb)}</td>
                <td className={cell}>{differs ? <span className="text-accent">changed</span> : <span className="text-muted-foreground">same</span>}</td>
              </tr>
            );
          })}
          {[["carbon", "Carbon cost"], ["physical", "Physical damage"], ["interruption", "Business interruption"]].map(([k, label]) => (
            <tr key={k} className="border-b border-border/50" data-testid={`sd-cmp-fig-${k}`}>
              <td className="px-2 py-1 text-muted-foreground">{label}</td>
              <td className={cell}>{sgd(fig(a, k))}</td>
              <td className={cell}>{sgd(fig(b, k))}</td>
              <td className={cell}>{delta(fig(a, k), fig(b, k))}</td>
            </tr>
          ))}
          <tr className="font-bold" data-testid="sd-cmp-total">
            <td className="px-2 py-1">Total modelled exposure</td>
            <td className={cell}>{sgd(a.total_sgd)}</td>
            <td className={cell}>{sgd(b.total_sgd)}</td>
            <td className={cell} data-testid="sd-cmp-total-delta">{delta(a.total_sgd, b.total_sgd)}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-[10px] text-muted-foreground mt-1.5">Rows highlighted in green show levers that differ between the two runs — the Δ column shows how much the outcome moved.</p>
    </div>
  );
}

function Calc({ calc, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button className="text-[10px] text-accent underline decoration-dotted flex items-center gap-0.5" onClick={() => setOpen(!open)} data-testid={testid}>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} How was this calculated?
      </button>
      {open && (
        <div className="mt-1.5 border border-border rounded-sm p-2 bg-surface space-y-1.5 text-[11px]" data-testid={`${testid}-panel`}>
          <div className="font-mono text-accent">{calc.formula}</div>
          {calc.inputs.map((i, k) => (
            <div key={k} className="flex justify-between gap-3">
              <span className="text-muted-foreground">{i.name} <span className="opacity-60">[{i.source}]</span></span>
              <span className="font-mono text-right">{i.value}</span>
            </div>
          ))}
          <div className="border-t border-border pt-1 space-y-0.5">
            {calc.steps.map((s, k) => <div key={k} className="font-mono text-[10px]">{s}</div>)}
          </div>
          <div className="font-mono font-bold">= {sgd(calc.result)}</div>
        </div>
      )}
    </div>
  );
}

export function ScenarioDetail({ kind, refId, title, onClose }) {
  const [d, setD] = useState(null);
  const [levers, setLevers] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [viewRun, setViewRun] = useState(null);
  const [compareIds, setCompareIds] = useState([]);

  const toggleCompare = (id) => {
    setCompareIds((c) => c.includes(id) ? c.filter((x) => x !== id) : c.length >= 2 ? [c[1], id] : [...c, id]);
  };

  const load = async () => {
    try {
      const r = await api.get(`/scenarios/detail/${kind}/${refId}`);
      setD(r.data);
      setLevers((lv) => lv || r.data.levers);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [kind, refId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setRunning(true); setResult(null); setViewRun(null);
    try {
      const r = await api.post("/scenarios/whatif", {
        kind, ref: refId,
        levers: { ...levers, carbon_price_sgd: +levers.carbon_price_sgd, hazard_uplift: +levers.hazard_uplift, share_affected_pct: +levers.share_affected_pct },
      });
      setResult(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    setRunning(false);
  };

  const saveRun = async () => {
    try {
      await api.post("/scenarios/whatif/save", {
        kind, ref: refId, title,
        levers: { ...levers, carbon_price_sgd: +levers.carbon_price_sgd, hazard_uplift: +levers.hazard_uplift, share_affected_pct: +levers.share_affected_pct },
        figures: result.figures, total_sgd: result.total_sgd, narrative: result.ai.text, ai_used: result.ai.used_llm,
      });
      toast.success("Simulation saved");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteRun = async (id) => {
    try {
      await api.delete(`/scenarios/whatif/saved/${id}`);
      if (viewRun?.id === id) setViewRun(null);
      setCompareIds((c) => c.filter((x) => x !== id));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const compared = compareIds.length === 2 && d
    ? compareIds.map((id) => d.saved_runs.find((r) => r.id === id)).filter(Boolean)
    : null;

  const shown = viewRun ? { figures: viewRun.figures, total_sgd: viewRun.total_sgd, ai: { text: viewRun.narrative, used_llm: viewRun.ai_used } } : result;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="scenario-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            {title}
            {d && <Badge variant={d.classification === "Physical" ? "danger" : "lime"} className="text-[9px]">{d.classification}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!d ? <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div> : (
          <div className="space-y-4 text-xs">
            <div>
              <div className="label-eyebrow mb-1">What this means</div>
              <p className="leading-relaxed" data-testid="sd-what">{d.what_it_means}</p>
            </div>
            <div className="border border-border rounded-sm p-2.5 bg-surface" data-testid="sd-source">
              <div className="label-eyebrow mb-1 flex items-center gap-1"><BookOpen className="h-3 w-3" /> Credible source</div>
              <div className="font-semibold">{d.source.name}</div>
              <p className="text-muted-foreground mt-0.5">{d.source.detail}</p>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Key assumptions</div>
              <div className="space-y-1" data-testid="sd-assumptions">
                {d.assumptions.map((a, i) => (
                  <div key={i} className="flex justify-between gap-3 border-b border-border/60 pb-1">
                    <span className="text-muted-foreground">{a.label} <span className="opacity-60">[{a.source}]</span></span>
                    <span className="font-mono text-right">{a.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">How it could affect the company</div>
              <p className="text-muted-foreground mb-1.5">{d.company_impact.summary}</p>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {d.company_impact.cost_lines.map((c) => <Badge key={c} variant="outline" className="text-[9px]">{c}</Badge>)}
              </div>
              {d.company_impact.exposed_facilities.length > 0 && (
                <div className="space-y-0.5" data-testid="sd-exposed">
                  {d.company_impact.exposed_facilities.map((f, i) => (
                    <div key={i} className="flex justify-between gap-2 font-mono text-[11px]">
                      <span>{f.name} — {f.hazard}</span>
                      <Badge variant={f.band === "High" || f.band === "Critical" ? "danger" : f.band.startsWith("Medium") ? "warning" : "outline"} className="text-[9px]">{f.band} · {f.score}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-accent/40 rounded-sm p-3 space-y-3" data-testid="sd-whatif">
              <div className="label-eyebrow flex items-center gap-1"><FlaskConical className="h-3.5 w-3.5 text-accent" /> What-if simulation — adjust levers, then run</div>
              {levers && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><Label className="text-[10px]">Carbon price (S$/t)</Label>
                    <Input type="number" className="h-7 text-xs" value={levers.carbon_price_sgd} onChange={(e) => setLevers({ ...levers, carbon_price_sgd: e.target.value })} data-testid="sd-lever-price" /></div>
                  <div><Label className="text-[10px]">Hazard uplift (+0–2)</Label>
                    <Input type="number" min={0} max={2} className="h-7 text-xs" value={levers.hazard_uplift} onChange={(e) => setLevers({ ...levers, hazard_uplift: e.target.value })} data-testid="sd-lever-uplift" /></div>
                  <div><Label className="text-[10px]">Operations affected (%)</Label>
                    <Input type="number" min={0} max={100} className="h-7 text-xs" value={levers.share_affected_pct} onChange={(e) => setLevers({ ...levers, share_affected_pct: e.target.value })} data-testid="sd-lever-share" /></div>
                  <div><Label className="text-[10px]">Mitigation in place</Label>
                    <Button size="sm" variant={levers.mitigation_in_place ? "primary" : "outline"} className="h-7 text-[10px] w-full"
                      onClick={() => setLevers({ ...levers, mitigation_in_place: !levers.mitigation_in_place })} data-testid="sd-lever-mitigation">
                      {levers.mitigation_in_place ? "Yes" : "No"}
                    </Button></div>
                </div>
              )}
              <Button size="sm" variant="primary" onClick={run} disabled={running} data-testid="sd-run-btn">
                <Sparkles className="h-3 w-3" /> {running ? "Running…" : "Run what-if simulation"}
              </Button>

              {shown && (
                <div className="space-y-3" data-testid="sd-result">
                  {viewRun && <Badge variant="outline" className="text-[9px]">Saved run · {new Date(viewRun.created_at).toLocaleString()} · {viewRun.created_by}</Badge>}
                  <div className="grid sm:grid-cols-3 gap-2">
                    {shown.figures.map((f) => (
                      <div key={f.key} className="border border-border rounded-sm p-2.5" data-testid={`sd-fig-${f.key}`}>
                        <div className="label-eyebrow text-[9px]">{f.label}</div>
                        <div className="font-mono font-bold text-base">{sgd(f.value_sgd)}</div>
                        <Calc calc={f.calc} testid={`sd-calc-${f.key}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between font-mono text-sm font-bold border-t border-border pt-2">
                    <span>Total modelled exposure {result?.revenue_context && !viewRun ? <span className="font-normal text-[10px] text-muted-foreground">({result.revenue_context})</span> : null}</span>
                    <span className="text-accent" data-testid="sd-total">{sgd(shown.total_sgd)}</span>
                  </div>
                  <div className="border border-[#38BDF8]/40 bg-[#38BDF8]/5 rounded-sm p-2.5" data-testid="sd-ai">
                    <div className="label-eyebrow text-[9px] mb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3 text-[#38BDF8]" /> AI interpretation {shown.ai.used_llm ? "(GPT — distinct from the computed figures above)" : "(fallback — computed figures only, AI unavailable)"}
                    </div>
                    <p className="whitespace-pre-line leading-relaxed">{shown.ai.text}</p>
                  </div>
                  {!viewRun && result && (
                    <Button size="sm" variant="outline" onClick={saveRun} data-testid="sd-save-run-btn"><Save className="h-3 w-3" /> Save simulation</Button>
                  )}
                </div>
              )}

              {d.saved_runs.length > 0 && (
                <div data-testid="sd-saved-runs">
                  <div className="label-eyebrow text-[9px] mb-1 flex items-center justify-between">
                    <span>Saved simulations ({d.saved_runs.length}){d.saved_runs.length >= 2 ? " — tick two to compare" : ""}</span>
                    {compareIds.length > 0 && (
                      <button className="text-accent underline decoration-dotted normal-case" onClick={() => setCompareIds([])} data-testid="sd-compare-clear">clear selection ({compareIds.length}/2)</button>
                    )}
                  </div>
                  {d.saved_runs.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 py-1 border-b border-border/60 last:border-0 text-[11px]">
                      <span className="flex items-center gap-2">
                        {d.saved_runs.length >= 2 && (
                          <input type="checkbox" className="accent-[#a3e635] h-3 w-3 cursor-pointer" checked={compareIds.includes(r.id)}
                            onChange={() => toggleCompare(r.id)} data-testid={`sd-compare-check-${r.id}`} title="Select for comparison" />
                        )}
                        <button className="underline decoration-dotted hover:text-accent text-left" onClick={() => { setViewRun(r); setResult(null); }} data-testid={`sd-view-run-${r.id}`}>
                          {new Date(r.created_at).toLocaleDateString()} · {sgd(r.total_sgd)} · S${r.levers.carbon_price_sgd}/t, +{r.levers.hazard_uplift} uplift, {r.levers.share_affected_pct}%{r.levers.mitigation_in_place ? ", mitigated" : ""}
                        </button>
                      </span>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => deleteRun(r.id)} data-testid={`sd-del-run-${r.id}`}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </div>
                  ))}
                  {compared && <CompareRuns a={compared[0]} b={compared[1]} />}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
